import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaDownload, FaFileExcel, FaTrash, FaUpload } from "react-icons/fa";
import {
  deleteImportBatch,
  downloadImportTemplate,
  getImportBatches,
  ImportBatch,
  ImportKind,
  ImportResult,
  uploadImport,
} from "../../../services/dataImportService";
import { crm } from "../../../theme";

interface ColumnInfo {
  header: string;
  required?: boolean;
  hint: string;
}

interface ImporterConfig {
  kind: ImportKind;
  title: string;
  description: string;
  templateFile: string;
  columns: ColumnInfo[];
  totalLabel: string;
}

// Espelha DataImportParser.FeeBillingColumns / OutboundInvoiceColumns (backend).
const IMPORTERS: ImporterConfig[] = [
  {
    kind: "fee-billings",
    title: "Honorários (faturamento)",
    description:
      "Alimenta Gestão › Comercial. Cada importação acrescenta linhas (não substitui); use o histórico abaixo para desfazer um lote.",
    templateFile: "modelo-honorarios.xlsx",
    totalLabel: "Honorário bruto",
    columns: [
      { header: "Data do faturamento", required: true, hint: "dd/mm/aaaa" },
      { header: "Cliente", required: true, hint: "Nome do cliente" },
      { header: "CNPJ do cliente", hint: "Vincula à empresa cadastrada" },
      { header: "Empresa", hint: "CVF Logística, CVF Distribuidora, CVF Rondônia ou W Trading" },
      { header: "Processo", hint: "Referência CVF (ex.: 1501 ou 1501A)" },
      { header: "Número da nota", hint: "Nota/fatura de honorários" },
      { header: "Honorário bruto", required: true, hint: "R$ (0 ou maior)" },
      { header: "Honorário líquido", required: true, hint: "R$ (0 ou maior)" },
      { header: "VMLD", hint: "R$ (0 ou maior)" },
      { header: "Agente comercial", hint: "Nome do agente" },
      { header: "Observações", hint: "Texto livre" },
    ],
  },
  {
    kind: "outbound-invoices",
    title: "NFs de saída (Distribuidora)",
    description:
      "Alimenta Gestão › Distribuidora. NFs com a mesma empresa + número + série já existentes são atualizadas (upsert).",
    templateFile: "modelo-nfs-de-saida.xlsx",
    totalLabel: "Valor total",
    columns: [
      { header: "Empresa", required: true, hint: "CVF Distribuidora, CVF Rondônia ou W Trading" },
      { header: "Número da NF", required: true, hint: "Até 30 caracteres" },
      { header: "Série", hint: "Até 10 caracteres" },
      { header: "Data de emissão", required: true, hint: "dd/mm/aaaa" },
      { header: "Cliente", required: true, hint: "Nome do cliente" },
      { header: "CNPJ do cliente", hint: "Vincula à empresa cadastrada" },
      { header: "Processo", hint: "Referência CVF (ex.: 1501 ou 1501A)" },
      { header: "Declaração (DI/DUIMP)", hint: "Até 50 caracteres" },
      { header: "Valor dos produtos", required: true, hint: "R$" },
      { header: "PIS", hint: "R$ (vazio = 0)" },
      { header: "COFINS", hint: "R$ (vazio = 0)" },
      { header: "IPI", hint: "R$ (vazio = 0)" },
      { header: "ICMS", hint: "R$ (vazio = 0)" },
      { header: "ICMS ST + FCP", hint: "R$ (vazio = 0)" },
      { header: "Valor total", required: true, hint: "R$" },
      { header: "Situação", hint: "Ativa (padrão) ou Estornada" },
    ],
  },
];

const brl = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
// setZone: mantém o dia informado mesmo se a API mandar a data com "Z" (sem converter pro fuso local)
const day = (value: string) => DateTime.fromISO(value, { setZone: true }).toFormat("dd/LL/yyyy");

const Importer = ({ config }: { config: ImporterConfig }) => {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [toDelete, setToDelete] = useState<ImportBatch | null>(null);

  const loadBatches = async () => {
    try {
      setBatches(await getImportBatches(config.kind));
    } catch {
      toast({ status: "error", title: "Não foi possível carregar o histórico de importações" });
    }
  };

  useEffect(() => {
    loadBatches();
  }, [config.kind]);

  const download = async () => {
    try {
      await downloadImportTemplate(config.kind, config.templateFile);
    } catch {
      toast({ status: "error", title: "Não foi possível baixar o modelo" });
    }
  };

  const upload = async (file?: File) => {
    if (!file) return;
    // Limpa já: escolher o mesmo arquivo de novo volta a disparar o onChange
    if (fileInput.current) fileInput.current.value = "";
    setUploading(true);
    setResult(null);
    try {
      const response = await uploadImport(config.kind, file);
      setResult(response);
      if (response.success) {
        toast({ status: "success", title: `${response.total} linha(s) importada(s)` });
        await loadBatches();
      }
    } catch {
      toast({ status: "error", title: "Não foi possível enviar o arquivo" });
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const target = toDelete;
    setToDelete(null);
    try {
      const response = await deleteImportBatch(config.kind, target.batchId);
      if (!response.success) {
        toast({ status: "warning", title: response.notifications?.[0] ?? "Não foi possível excluir o lote" });
      } else {
        toast({ status: "success", title: "Lote excluído" });
      }
      await loadBatches();
    } catch {
      toast({ status: "error", title: "Não foi possível excluir o lote" });
    }
  };

  return (
    <Box border={`1px solid ${crm.border}`} borderRadius="12px" p="20px" mb="24px" bg="white">
      <Flex justify="space-between" align="flex-start" gap="12px" wrap="wrap">
        <Box maxW="640px">
          <Heading size="md" fontWeight="semibold">{config.title}</Heading>
          <Text fontSize="sm" color={crm.textMuted} mt="4px">{config.description}</Text>
        </Box>
        <Flex gap="8px" wrap="wrap">
          <Button
            leftIcon={<FaDownload />}
            variant="outline"
            color={crm.brand}
            borderColor={crm.brand}
            borderRadius="10px"
            _hover={{ bg: crm.brandSoft }}
            onClick={download}
          >
            Baixar modelo
          </Button>
          <Button
            leftIcon={<FaUpload />}
            bg={crm.brand}
            color="white"
            borderRadius="10px"
            _hover={{ bg: crm.brandDark }}
            isLoading={uploading}
            onClick={() => fileInput.current?.click()}
          >
            Importar planilha
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </Flex>
      </Flex>

      <Button size="xs" variant="link" color={crm.brand} mt="8px" onClick={() => setShowColumns((v) => !v)}>
        {showColumns ? "Ocultar colunas do modelo" : "Ver colunas do modelo"}
      </Button>
      {showColumns && (
        <Box mt="8px" border={`1px solid ${crm.border}`} borderRadius="10px" overflowX="auto">
          <Table size="sm">
            <Thead bg={crm.subtle}>
              <Tr>
                <Th textTransform="none">Coluna</Th>
                <Th textTransform="none">Obrigatória</Th>
                <Th textTransform="none">Formato</Th>
              </Tr>
            </Thead>
            <Tbody>
              {config.columns.map((column) => (
                <Tr key={column.header}>
                  <Td fontWeight="600">{column.header}</Td>
                  <Td>{column.required ? "Sim" : "Não"}</Td>
                  <Td color={crm.textMuted}>{column.hint}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Text fontSize="xs" color={crm.textMuted} p="8px">
            Cabeçalho na linha 1 (não altere os nomes), dados a partir da linha 2, máximo de 5.000 linhas. Se houver
            qualquer erro, nada é importado.
          </Text>
        </Box>
      )}

      {result && !result.success && (
        <Alert status="error" mt="16px" borderRadius="10px" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight="600" mb="4px">
              Nada foi importado: {result.errors.length} erro(s) encontrados. Corrija a planilha e envie novamente.
            </Text>
            <Box maxH="220px" overflowY="auto" fontSize="sm">
              {result.errors.map((error, index) => (
                <Text key={index}>
                  {error.row > 0 ? `Linha ${error.row}: ` : ""}
                  {error.message}
                </Text>
              ))}
            </Box>
          </AlertDescription>
        </Alert>
      )}

      {result?.success && (
        <Alert status={result.warnings.length > 0 ? "warning" : "success"} mt="16px" borderRadius="10px" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight="600">
              {result.total} linha(s) importada(s)
              {result.updated > 0 ? `: ${result.inserted} nova(s), ${result.updated} atualizada(s)` : ""}.
              {result.warnings.length > 0 ? ` ${result.warnings.length} aviso(s) de vínculo:` : ""}
            </Text>
            {result.warnings.length > 0 && (
              <Box maxH="180px" overflowY="auto" fontSize="sm" mt="4px">
                {result.warnings.map((warning, index) => (
                  <Text key={index}>
                    Linha {warning.row}: {warning.message}
                  </Text>
                ))}
              </Box>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Text fontSize="sm" fontWeight="600" mt="20px" mb="8px">
        Histórico de importações
      </Text>
      {batches.length === 0 ? (
        <Text fontSize="sm" color={crm.textMuted}>Nenhuma importação ativa.</Text>
      ) : (
        <Box border={`1px solid ${crm.border}`} borderRadius="10px" overflowX="auto">
          <Table size="sm">
            <Thead bg={crm.subtle}>
              <Tr>
                <Th textTransform="none">Importado em</Th>
                <Th textTransform="none">Período</Th>
                <Th textTransform="none" isNumeric>Linhas</Th>
                <Th textTransform="none" isNumeric>{config.totalLabel}</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {batches.map((batch) => (
                <Tr key={batch.batchId} _hover={{ bg: crm.brandSoft }}>
                  <Td>{DateTime.fromISO(batch.importedAt).toFormat("dd/LL/yyyy HH:mm")}</Td>
                  <Td>{day(batch.periodFrom)} a {day(batch.periodTo)}</Td>
                  <Td isNumeric>{batch.rows}</Td>
                  <Td isNumeric>{brl(batch.total)}</Td>
                  <Td textAlign="right">
                    <IconButton
                      aria-label="Excluir lote"
                      icon={<FaTrash />}
                      size="xs"
                      variant="ghost"
                      color={crm.danger}
                      onClick={() => setToDelete(batch)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <AlertDialog isOpen={!!toDelete} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir lote</AlertDialogHeader>
            <AlertDialogBody>
              Excluir as {toDelete?.rows} linha(s) deste lote? Os painéis deixam de considerá-las.
              {config.kind === "outbound-invoices" &&
                " Só as NFs criadas por este lote são excluídas; NFs de lotes anteriores que ele atualizou continuam, com os valores desta importação."}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setToDelete(null)}>Cancelar</Button>
              <Button colorScheme="red" ml={3} onClick={confirmDelete}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

const Imports = () => (
  <Box>
    <Heading size="lg" fontWeight="normal" mb="8px">
      <Flex alignItems="center" gap="12px">
        <FaFileExcel color="var(--icon-color)" />
        Importações
      </Flex>
    </Heading>
    <Text fontSize="sm" color={crm.textMuted} mb="24px">
      Importe planilhas .xlsx no modelo fixo abaixo. Baixe o modelo, preencha e envie.
    </Text>
    {IMPORTERS.map((config) => (
      <Importer key={config.kind} config={config} />
    ))}
  </Box>
);

export default Imports;
